import { useState, useEffect, useMemo } from "react";
import { Outlet, useOutlet, useSearchParams } from "react-router-dom";
import { CarouselStyles, TextStyles, FeedbackStyles } from "styles/style";
import BaseView from "./BaseView";
import { api } from "supabase/database/api.ts";
import { Carousel } from "components/base/VideoCarousel";

export default function VideoView({
    title = null,
    description = null,
    list = null,
    table = null,
    url = null,
    sort_by = null
}) {
    const [material, setMaterial] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const monthsPerPage = 3;
    const [_, setCapLength] = useState(63);
    const hasChild = useOutlet();

    // Get page and search from URL params
    const currentPage = parseInt(searchParams.get('page') || '1', 10);
    const currentSearch = searchParams.get('search') || '';

    // Fetch all data once
    useEffect(() => {
      async function prepMaterial() {
        if (list) {
          setMaterial(list);
        } else if (table) {
          const data = await api.contentQueries.getContentList({ table, sort_by: sort_by ?? undefined }, true);
          setMaterial(data);
        }
        setLoading(false);
      }
      prepMaterial();
    }, [list, table]);

    // Handle search change
    const handleSearchChange = (newSearch) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            if (newSearch.trim()) {
                newParams.set('search', newSearch);
            } else {
                newParams.delete('search');
            }
            // Always reset to page 1 when searching
            if (newSearch.trim() !== currentSearch) {
                newParams.set('page', '1');
            }
            return newParams;
        }, { replace: true });
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('page', newPage.toString());
            return newParams;
        });
    };

    // Responsive title length
    useEffect(() => {
      const updateCapLength = () => {
        const width = window.innerWidth;
        if (width <= 640) setCapLength(63);
        else if (width <= 768) setCapLength(110);
        else setCapLength(180);
      };
      updateCapLength();
      window.addEventListener("resize", updateCapLength);
      return () => window.removeEventListener("resize", updateCapLength);
    }, []);

    const groupVideosByMonth = (videos) => {
      return videos.reduce((acc, video) => {
        let key;

        if (video.section) {
          key = video.section;
        } else if (video.date) {
          const dateObj = new Date(video.date.replace(/-dr$/, ''));
          if (isNaN(dateObj.getTime())) return acc;
          const monthYear = dateObj.toLocaleString('en-US', { month: 'long' });
          if (monthYear === 'Invalid Date') return acc;
          key = monthYear;
        } else {
          return acc;
        }

        if (!acc[key]) acc[key] = [];
        acc[key].push(video);
        return acc;
      }, {});
    };

    // Filter videos and group by month
    const filteredAndGrouped = useMemo(() => {
      const filtered = material.filter((item) =>
        item.title?.toLowerCase().includes(currentSearch.toLowerCase())
        || item.date?.toLowerCase().includes(currentSearch.toLowerCase())
      );
      return groupVideosByMonth(filtered);
    }, [material, currentSearch]);

    // Paginate months
    const paginatedMonths = useMemo(() => {
      const monthEntries = Object.entries(filteredAndGrouped);
      const start = (currentPage - 1) * monthsPerPage;
      return monthEntries.slice(start, start + monthsPerPage);
    }, [filteredAndGrouped, currentPage]);

    const totalVideos = useMemo(() => {
      return Object.values(filteredAndGrouped).flat().length;
    }, [filteredAndGrouped]);

    const totalMonths = Object.keys(filteredAndGrouped).length;

    return (
        <>
        {hasChild ? (
            <Outlet />
        ) : (
            <BaseView
                title={title}
                description={description}
                listCount={totalMonths}
                search={currentSearch}
                onSearchChange={handleSearchChange}
                page={currentPage}
                onPageChange={handlePageChange}
                pageSize={monthsPerPage}
            >
                {loading ? (
                    <>
                        {Array.from({ length: 2 }).map((_, gi) => (
                            <div key={gi}>
                                <CarouselStyles.CarouselPost>
                                    <TextStyles.Info>
                                        <FeedbackStyles.SkeletonHeading />
                                    </TextStyles.Info>
                                </CarouselStyles.CarouselPost>
                                <div className="m-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {Array.from({ length: 4 }).map((_, i) => (
                                        <div key={i} className="p-2">
                                            <FeedbackStyles.SkeletonBlock />
                                            <FeedbackStyles.SkeletonLine />
                                            <FeedbackStyles.SkeletonShortLine />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </>
                ) : paginatedMonths.length === 0 ? (
                    <FeedbackStyles.EmptyStateContainer>
                        <FeedbackStyles.EmptyStateTitle>No videos found</FeedbackStyles.EmptyStateTitle>
                        {currentSearch && (
                            <FeedbackStyles.EmptyStateHint>Try a different search term</FeedbackStyles.EmptyStateHint>
                        )}
                    </FeedbackStyles.EmptyStateContainer>
                ) : (
                    <>
                        {paginatedMonths.map(([month, videos]) => (
                            <div key={month}>
                                <CarouselStyles.CarouselPost>
                                    <TextStyles.Info>
                                        <TextStyles.Title>{month}</TextStyles.Title>
                                    </TextStyles.Info>
                                </CarouselStyles.CarouselPost>
                                <Carousel videoList={videos} month={month} />
                            </div>
                        ))}
                    </>
                )}
            </BaseView>
        )}
        </>
    )
}
